/**
 * THE CREW ROSTER, EDITABLE — the screen Jacky went looking for.
 *
 * *"where do I manage current active installer, i see a installer page, where do I manage the data"*,
 * and then, on the eleven crews the old platform left: *"I dont want 11 crew … I want to start
 * fresh."* Both were true: the Installers page read `portal.installer_party` and nothing anywhere
 * could write it.
 *
 * ONE FORM FOR ADD AND EDIT, and the things it is careful about:
 *   · **Archiving, not deleting.** A crew carries schedule history (YU 56 crew-days, Ryan 37); the
 *     archive keeps the name on those days and stops the crew appearing for scheduling or signing in.
 *     A reason is required, because "why is this crew gone" gets asked months later.
 *   · **The mobile is the login.** It is stored as the office types it and normalised server-side;
 *     the portal texts a code to that number, so this field is what makes the crew's sign-in work.
 *   · **Nothing is typed that can be picked.** Jacky, 18 September: *"language / what they lay should be
 *     a drop down and tick choice. not typed."* — so the language is one of three choices, and what
 *     they lay is ticked from the categories the catalogue actually sells.
 *   · **The short code is gone.** *"i dont like this shortcode |"*. It was a column in the roster, a
 *     row on the crew page and a field here; the roster derives it from the name now (the schedule
 *     labels it appears in are history, and a crew should not have to invent an abbreviation to be
 *     added), so an existing code is preserved on save but never asked for again.
 */
import {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button} from '@aiw/ui';
import {CREW_LANGUAGE_CHOICES, choiceFromLanguages, crewCalendarUrl, languagesFromChoice, type CrewLanguageChoice} from '@artemis-group/aiw-contracts';
import {api, authHeaders, client, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {inputCls} from './job-bits';

export type Crew = {
  id: string; name: string; short_name: string | null; kind: string; labour_mode?: string | null;
  phone?: string | null; abn?: string | null; bank_account_name?: string | null; bank_bsb?: string | null;
  bank_account_number?: string | null; price_basis?: string | null; categories?: string[] | null; payment_preference?: 'cash' | 'transfer' | null;
  languages?: string[] | null; active: boolean;
};

/** What the detail read returns about the crew's calendar address. */
export type CrewCalendarState = {
  token: string | null; issued_at: string | null; rotated_at: string | null;
  revoked_at: string | null; last_read_at: string | null; reads: number;
};

const KINDS = ['internal_crew', 'external_subcontractor'] as const;
const LABOUR = ['team_artemis_zero', 'catalog_lab_cost', 'declared_rates'] as const;

export function CrewEditor({crew, onSaved, onCancel}: {
  /** Null adds a crew; a crew edits that one. */
  crew: Crew | null;
  onSaved: (message: string) => void;
  onCancel: () => void;
}) {
  const {t} = useT();
  const [form, setForm] = useState({
    name: crew?.name ?? '',
    account_name:'',
    account_role:'worker' as 'worker'|'manager',
    short_name: crew?.short_name ?? '',
    kind: (crew?.kind as typeof KINDS[number]) ?? 'internal_crew',
    // Read from the crew, not assumed: this was hardcoded to team_artemis_zero, so opening a
    // subcontractor with declared rates and pressing save silently made them an Artemis crew.
    labour_mode: (crew?.labour_mode as typeof LABOUR[number]) ?? 'team_artemis_zero',
    phone: crew?.phone ?? '',
    abn: crew?.abn ?? '',
    bank_account_name: crew?.bank_account_name ?? '',
    bank_bsb: crew?.bank_bsb ?? '',
    bank_account_number: crew?.bank_account_number ?? '',
    price_basis: (crew?.price_basis as 'list' | 'custom') ?? 'list',
    payment_preference: (crew?.payment_preference ?? '') as '' | 'cash' | 'transfer',
    languages: choiceFromLanguages(crew?.languages ?? ['en']) as CrewLanguageChoice,
  });
  const [categories, setCategories] = useState<string[]>(crew?.categories ?? []);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const set = (patch: Partial<typeof form>) => setForm((f) => ({...f, ...patch}));

  // What they lay: the catalogue's own floor types. Read once per screen; a failure leaves the ticks
  // that are already on the record, so a slow catalogue never blocks saving a crew.
  const catalogue = useQuery({
    queryKey: ['crew-categories'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const r = await fetchImpl(`${env.apiBase}/api/installers/categories`, {headers: await authHeaders()});
      if (!r.ok) throw new Error('catalogue unavailable');
      return ((await r.json()) as {ok: true; data: string[]}).data;
    },
  });
  const options = [...(catalogue.data ?? []), ...categories.filter((c) => !(catalogue.data ?? []).includes(c))];

  async function send(active: boolean) {
    if (form.name.trim().length < 2) { setNotice(t('crew.needName')); return; }
    if (!crew && active && !form.phone.trim()) { setNotice(t('crew.needPhone')); return; }
    if (!active && reason.trim().length < 3) { setNotice(t('crew.needReason')); return; }
    setBusy(true); setNotice('');
    try {
      const r = await api.propose(client, {
        command: 'save_installer_party',
        args: {
          id: crew?.id ?? null,
          ...(!crew?{account_name:form.account_name.trim()||null,account_role:form.account_role}:{}),
          name: form.name.trim(),
          // Carried through unchanged and never asked for: the code labels days already worked, and a
          // new crew's is derived server-side from the name.
          short_name: form.short_name.trim() || null,
          kind: form.kind,
          labour_mode: form.labour_mode,
          phone: form.phone.trim() || null,
          abn: form.abn.trim() || null,
          bank_account_name: form.bank_account_name.trim() || null,
          bank_bsb: form.bank_bsb.trim() || null,
          bank_account_number: form.bank_account_number.trim() || null,
          price_basis: form.price_basis,
          payment_preference: form.payment_preference || null,
          categories: categories.length ? categories : null,
          languages: languagesFromChoice(form.languages),
          active,
          reason: reason.trim() || null,
        },
        idempotency_key: `crew:${crew?.id ?? 'new'}:${form.name}:${active}`,
      });
      if (r.status === 'executed') {
        const result = r.result as {created?: boolean; archived?: boolean} | undefined;
        onSaved(result?.archived ? t('crew.archived', {name: form.name}) : result?.created ? t('crew.added', {name: form.name}) : t('crew.saved', {name: form.name}));
      } else if (r.status === 'pending') setNotice(t('crew.pending'));
      else setNotice(t('crew.refused', {reason: r.reason ?? ''}));
    } catch (e) {
      setNotice(t('crew.failed', {message: (e as Error).message}));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof typeof form, label: string, extra: Record<string, unknown> = {}) =>
    <label className="text-sm" key={key}>{label}
      <input className={`${inputCls} mt-1 w-full`} value={form[key]} onChange={(e) => set({[key]: e.target.value} as Partial<typeof form>)} {...extra} />
    </label>;

  return <div className="space-y-3 rounded-lg border bg-card p-4">
    <h2 className="font-display text-sm font-semibold">{crew ? t('crew.editTitle', {name: crew.name}) : t('crew.addTitle')}</h2>
    <div className="grid gap-3 sm:grid-cols-3">
      {field('name', t('crew.name'))}
      <label className="text-sm">{t('crew.kind')}
        <select className={`${inputCls} mt-1 w-full`} value={form.kind} onChange={(e) => set({kind: e.target.value as typeof form.kind})}>
          {KINDS.map((k) => <option key={k} value={k}>{t(k === 'internal_crew' ? 'crew.internal' : 'crew.external')}</option>)}
        </select>
      </label>
      {field('phone', t('crew.mobile'), {type: 'tel', inputMode: 'tel', placeholder: '0412 345 678'})}
      <label className="text-sm">{t('crew.languages')}
        <select className={`${inputCls} mt-1 w-full`} value={form.languages} onChange={(e) => set({languages: e.target.value as CrewLanguageChoice})}>
          {CREW_LANGUAGE_CHOICES.map((c) => <option key={c} value={c}>{t(`crew.lang.${c}`)}</option>)}
        </select>
      </label>
      <label className="text-sm">{t('crew.priceBasis')}
        <select className={`${inputCls} mt-1 w-full`} value={form.price_basis} onChange={(e) => set({price_basis: e.target.value as 'list' | 'custom'})}>
          <option value="list">{t('crew.basisList')}</option>
          <option value="custom">{t('crew.basisCustom')}</option>
        </select>
      </label>
      <label className="text-sm">{t('crew.labourMode')}
        <select className={`${inputCls} mt-1 w-full`} value={form.labour_mode} onChange={(e) => set({labour_mode: e.target.value as typeof form.labour_mode})}>
          {LABOUR.map((m) => <option key={m} value={m}>{t(`crew.labour.${m}`)}</option>)}
        </select>
      </label>
      {/* Jacky, 1 October 2026: "Installer have preference of been paid … registered under installer account, Cash or Transfer". */}
      <label className="text-sm">{t('crew.paymentPreference')}
        <select className={`${inputCls} mt-1 w-full`} value={form.payment_preference} onChange={(e) => set({payment_preference: e.target.value as typeof form.payment_preference})}>
          <option value="">{t('crew.pay.unset')}</option>
          <option value="cash">{t('crew.pay.cash')}</option>
          <option value="transfer">{t('crew.pay.transfer')}</option>
        </select>
      </label>
      {field('abn', t('crew.abn'), {inputMode: 'numeric'})}
      {field('bank_account_name', t('crew.bankName'))}
      {field('bank_bsb', t('crew.bsb'), {inputMode: 'numeric', placeholder: '062-000'})}
      {field('bank_account_number', t('crew.account'), {inputMode: 'numeric'})}
    </div>

    {/* What they lay — ticked, never typed. */}
    <fieldset className="rounded border p-3">
      <legend className="px-1 text-sm font-medium">{t('crew.categories')}</legend>
      <p className="mb-2 text-micro text-muted-foreground">{catalogue.isPending ? t('crew.categoriesLoading') : t('crew.categoriesNote')}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((c) => <label key={c} className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="h-4 w-4" checked={categories.includes(c)}
            onChange={(e) => setCategories((held) => e.target.checked ? [...held, c] : held.filter((x) => x !== c))} />
          {c}
        </label>)}
      </div>
    </fieldset>

    {crew && <label className="block text-sm">{t('crew.archiveReason')}
      <input className={`${inputCls} mt-1 w-full`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder={t('crew.archivePlaceholder')} />
    </label>}
      {!crew&&form.kind==='external_subcontractor'&&<div className="mt-3 space-y-2"><label className="block text-sm">{t('crew.accountName')}<input className={inputCls} value={form.account_name} onChange={e=>set({account_name:e.target.value})}/></label><label className="block text-sm">{t('crew.accountRole')}<select className="ml-2 rounded border p-2" value={form.account_role} onChange={e=>set({account_role:e.target.value as 'worker'|'manager'})}><option value="worker">{t('crew.accountWorker')}</option><option value="manager">{t('crew.accountManager')}</option></select></label></div>}
    <div className="flex flex-wrap items-center gap-3">
      <Button disabled={busy} onClick={() => void send(true)}>{busy ? t('crew.saving') : crew ? t('crew.save') : t('crew.add')}</Button>
      {crew?.active && <Button variant="outline" disabled={busy} onClick={() => void send(false)}>{t('crew.archive')}</Button>}
      <Button variant="ghost" disabled={busy} onClick={onCancel}>{t('common.close')}</Button>
      <span className="text-micro text-muted-foreground">{t('crew.phoneNote')}</span>
    </div>
    {notice && <p role="status" className="rounded border p-3 text-sm">{notice}</p>}
  </div>;
}

/**
 * THE CREW'S OWN CALENDAR, from the office side.
 *
 * Jacky, 18 September: *"each crew should also have a subscription calendar that is generated, so they
 * can click and add it to calendar."* What the office needs from this block is the two facts it cannot
 * get any other way — whether an address exists, and whether a phone has ever actually fetched it —
 * plus the three acts: create, replace, stop.
 *
 * The link is a capability, so it is shown IN FULL and copyable rather than hidden behind a "send"
 * button: the crew may want it pasted into their phone in front of you, and EmmA sends the same string.
 */
export function CrewCalendar({crew, calendar, onNotice}: {crew: {id: string; name: string}; calendar: CrewCalendarState | null; onNotice: (m: string) => void}) {
  const {t} = useT();
  const [state, setState] = useState<CrewCalendarState | null>(calendar);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => setState(calendar), [calendar?.token, calendar?.revoked_at, calendar?.last_read_at]);

  const url = state?.token ? crewCalendarUrl(env.apiBase, state.token, window.location.origin) : null;

  async function act(action: 'mint' | 'rotate' | 'revoke') {
    setBusy(true);
    try {
      const r = await api.propose(client, {
        command: 'save_crew_calendar_link',
        args: {party_id: crew.id, action},
        idempotency_key: `crew-calendar:${crew.id}:${action}:${state?.token ?? 'none'}`,
      });
      if (r.status === 'executed') {
        const result = r.result as {token?: string; url?: string | null; revoked_at?: string | null} | undefined;
        setState((held) => ({
          token: action === 'revoke' ? null : result?.token ?? null,
          issued_at: held?.issued_at ?? new Date().toISOString(),
          rotated_at: action === 'rotate' ? new Date().toISOString() : held?.rotated_at ?? null,
          revoked_at: action === 'revoke' ? new Date().toISOString() : null,
          last_read_at: null,
          reads: 0,
        }));
        onNotice(action === 'revoke' ? t('crew.calendarRevoke') : result?.url ?? '');
      } else if (r.status === 'pending') onNotice(t('crew.pending'));
      else onNotice(t('crew.refused', {reason: r.reason ?? ''}));
    } catch (e) {
      onNotice(t('crew.calendarFailed', {message: (e as Error).message}));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      onNotice(t('crew.calendarCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onNotice(url);
    }
  }

  const stamp = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-AU', {day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit'}) : '—');

  return <section className="rounded-lg border bg-card p-4">
    <h2 className="font-display text-sm font-semibold">{t('crew.calendar')}</h2>
    <p className="mb-3 text-micro text-muted-foreground">{t('crew.calendarWhy')}</p>
    {url ? <>
      {/* WHAT HE ASKED TO SEE: the URL, the token that was issued, when, and until when it works. */}
      <dl className="mb-3 space-y-1 text-sm">
        <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('crew.calendarIssued')}</dt><dd>{stamp(state?.issued_at)}</dd></div>
        {state?.rotated_at && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('crew.calendarRotated')}</dt><dd>{stamp(state.rotated_at)}</dd></div>}
        <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('crew.calendarExpires')}</dt><dd>{t('crew.calendarNoExpiry')}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('crew.calendarToken')}</dt><dd className="break-all font-mono text-micro">{state?.token}</dd></div>
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded border bg-muted/40 px-2 py-1 text-micro">{url}</code>
        <Button variant="outline" disabled={busy} onClick={() => void copy()}>{copied ? t('crew.calendarCopied') : t('crew.calendarCopy')}</Button>
      </div>
      <p className="mt-2 text-micro text-muted-foreground">{t('crew.calendarPaste')}</p>
      <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
        <a className="underline" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer">{t('crew.calendarGoogle')}</a>
        <span className="text-micro text-muted-foreground">{state?.reads ? t('crew.calendarRead', {date: stamp(state.last_read_at), count: state.reads}) : t('crew.calendarNeverRead')}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" disabled={busy} onClick={() => void act('rotate')}>{busy ? t('crew.calendarBusy') : t('crew.calendarRotate')}</Button>
        <Button variant="ghost" disabled={busy} onClick={() => void act('revoke')}>{t('crew.calendarRevoke')}</Button>
        <span className="text-micro text-muted-foreground">{t('crew.calendarRotateWhy')}</span>
      </div>
    </> : <>
      <p className="text-sm">{state?.revoked_at ? t('crew.calendarRevoked', {date: stamp(state.revoked_at)}) : t('crew.calendarNone')}</p>
      <Button className="mt-2" variant="outline" disabled={busy} onClick={() => void act('mint')}>{busy ? t('crew.calendarBusy') : t('crew.calendarIssue')}</Button>
    </>}
  </section>;
}

export function AddCrewButton({onSaved}: {onSaved: (message: string) => void}) {
  const {t} = useT();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');
  if (!open) return <div className="mb-3 flex items-center gap-3">
    <Button variant="outline" onClick={() => setOpen(true)}>{t('crew.add')}</Button>
    {notice && <span role="status" className="text-sm">{notice}</span>}
  </div>;
  return <div className="mb-3">
    <CrewEditor crew={null} onCancel={() => setOpen(false)} onSaved={(m) => { setNotice(m); setOpen(false); onSaved(m); }} />
  </div>;
}
